import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './ItemSearch.css';

function ItemSearch() {
  const { apiFetch, hasPermission } = useAuth();
  const { language } = useI18n();
  const canEditItems = hasPermission('items.edit');

  const t = language === 'da'
    ? {
        validAmount: 'Indtast et gyldigt beloeb for denne handling.',
        failedSearch: 'Kunne ikke søge items',
        failedUpdate: 'Kunne ikke opdatere item.',
        updated: 'Opdaterede {item} i {location}: {prev} -> {next}',
        title: 'Item overblik',
        subtitle: 'Find og live-administrer items i player inventories, bagagerum, handskerum og huse.',
        searchPlaceholder: 'Indtast item navn (bread, phone, weapon_pistol...)',
        clearSearch: 'Ryd søgning',
        searching: 'søger...',
        search: 'søg',
        recent: 'Seneste:',
        advancedFilters: 'Avancerede filtre',
        playerIdentifier: 'Spiller identifikator',
        minQuantity: 'Min. antal',
        maxQuantity: 'Maks. antal',
        ownerIdentifier: 'Filtrer via ejer-identifikator',
        minimumAmount: 'Minimum beloeb',
        maximumAmount: 'Maksimum beloeb',
        actionFailed: 'Handling fejlede',
        updatedLabel: 'Opdateret',
        storageHits: 'Lagertraef',
        totalQuantity: 'Samlet antal',
        item: 'Item',
        distributionByLocation: 'Fordeling efter placering',
        liveItemControls: 'Live item-kontroller',
        viewOnly: 'Du har kun visningsadgang. Bed om <code>items.edit</code> for live-kontroller.',
        qty: 'antal',
        owner: 'Ejer',
        identifier: 'Identifikator',
        storageId: 'Lager-ID',
        liveActions: 'Live handlinger',
        amount: 'Beloeb',
        add: 'tilføj',
        remove: 'Fjern',
        set: 'sæt',
        applying: 'Anvender opdatering...',
        noResults: 'Ingen resultater fundet',
        noResultsMessage: 'Intet lager indeholder {item} med de nuvaerende filtre.',
        newSearch: 'Start ny søgning',
        results: 'resultater',
        sortBy: 'Sorter efter',
        sortOwner: 'Ejer navn',
        sortQuantity: 'Antal',
        sortLocation: 'Placering',
        sortStorageType: 'Lagertype',
        ascending: 'stigende',
        descending: 'faldende',
        exactMatch: 'Nøjagtigt match',
        caseSensitive: 'Versalfølsom',
        seeMore: 'Se mere',
        seeLess: 'Vis mindre',
        copy: 'Kopiér',
        copied: 'Kopieret',
        metadata: 'Metadata',
        serial: 'Serienummer',
        durability: 'Holdbarhed',
        registered: 'Registreret til',
        ammo: 'Ammunition',
        components: 'Komponenter',
        noMetadata: 'Ingen metadata',
        metadataSearch: 'Søg i metadata',
        metadataField: 'Metadata felt',
        metadataValue: 'Søgværdi'
      }
    : {
        validAmount: 'Enter a valid amount for this action.',
        failedSearch: 'Failed to search items',
        failedUpdate: 'Failed to update item.',
        updated: 'Updated {item} in {location}: {prev} -> {next}',
        title: 'Item Overview',
        subtitle: 'Find and live-manage items in player inventories, trunks, gloveboxes, and house stashes.',
        searchPlaceholder: 'Enter item name (bread, phone, weapon_pistol...)',
        clearSearch: 'Clear search',
        searching: 'Searching...',
        search: 'Search',
        recent: 'Recent:',
        advancedFilters: 'Advanced Filters',
        playerIdentifier: 'Player Identifier',
        minQuantity: 'Min Quantity',
        maxQuantity: 'Max Quantity',
        ownerIdentifier: 'Filter by owner identifier',
        minimumAmount: 'Minimum amount',
        maximumAmount: 'Maximum amount',
        actionFailed: 'Action Failed',
        updatedLabel: 'Updated',
        storageHits: 'Storage Hits',
        totalQuantity: 'Total Quantity',
        item: 'Item',
        distributionByLocation: 'Distribution by Location',
        liveItemControls: 'Live Item Controls',
        viewOnly: 'You have view access only. Ask for <code>items.edit</code> to use live controls.',
        qty: 'qty',
        owner: 'Owner',
        identifier: 'Identifier',
        storageId: 'Storage ID',
        liveActions: 'Live Actions',
        amount: 'Amount',
        add: 'Add',
        remove: 'Remove',
        set: 'Set',
        applying: 'Applying update...',
        noResults: 'No Results Found',
        noResultsMessage: 'No storage contains {item} with the current filters.',
        newSearch: 'Start New Search',
        results: 'results',
        sortBy: 'Sort by',
        sortOwner: 'Owner name',
        sortQuantity: 'Quantity',
        sortLocation: 'Location',
        sortStorageType: 'Storage Type',
        ascending: 'ascending',
        descending: 'descending',
        exactMatch: 'Exact Match',
        caseSensitive: 'Case Sensitive',
        seeMore: 'See More',
        seeLess: 'See Less',
        copy: 'Copy',
        copied: 'Copied',
        metadata: 'Metadata',
        serial: 'Serial',
        durability: 'Durability',
        registered: 'Registered To',
        ammo: 'Ammo',
        components: 'Components',
        noMetadata: 'No Metadata',
        metadataSearch: 'Search Metadata',
        metadataField: 'Metadata Field',
        metadataValue: 'Search Value'
      };

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

  const [itemName, setItemName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [filterOver, setFilterOver] = useState('');
  const [filterUnder, setFilterUnder] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [updatingKey, setUpdatingKey] = useState('');
  const [actionAmounts, setActionAmounts] = useState({});
  const [sortBy, setSortBy] = useState('quantity'); // 'quantity', 'owner', 'location', 'storageType'
  const [sortDir, setSortDir] = useState('desc'); // 'asc', 'desc'
  const [exactMatch, setExactMatch] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [metadataModal, setMetadataModal] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [searchMetadata, setSearchMetadata] = useState(false);
  const [metadataField, setMetadataField] = useState('serial'); // 'serial', 'registered', 'durability'
  const [metadataValue, setMetadataValue] = useState('');
  const [expandedIdentifiers, setExpandedIdentifiers] = useState({});
  const [copiedIdentifierKey, setCopiedIdentifierKey] = useState('');
  const [recentSearches, setRecentSearches] = useState(() => {
    const saved = localStorage.getItem('recentItemSearches');
    return saved ? JSON.parse(saved) : [];
  });

  const makeResultKey = (result) => `${result.storageType}:${result.storageId}:${result.item || itemName}`;

  const runSearch = async (queryItem) => {
    if (!queryItem.trim()) return;

    setLoading(true);
    setError(null);
    setSuccessMessage('');
    setResults([]);
    setSearchPerformed(true);

    const newRecent = [queryItem, ...recentSearches.filter((s) => s !== queryItem)].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('recentItemSearches', JSON.stringify(newRecent));

    try {
      let url = `/api/items/search?item=${encodeURIComponent(queryItem)}`;
      if (identifier) url += `&identifier=${encodeURIComponent(identifier)}`;
      if (filterOver) url += `&over=${filterOver}`;
      if (filterUnder) url += `&under=${filterUnder}`;
      if (exactMatch) url += `&exact=1`;
      if (caseSensitive) url += `&caseSensitive=1`;
      if (searchMetadata && metadataValue) url += `&metadataField=${metadataField}&metadataValue=${encodeURIComponent(metadataValue)}`;

      const response = await apiFetch(url);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedSearch);
      }

      const data = await response.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    runSearch(itemName);
  };

  const handleQuickSearch = (item) => {
    setItemName(item);
    runSearch(item);
  };

  const clearSearch = () => {
    setItemName('');
    setIdentifier('');
    setFilterOver('');
    setFilterUnder('');
    setSearchMetadata(false);
    setMetadataValue('');
    setMetadataField('serial');
    setResults([]);
    setError(null);
    setSuccessMessage('');
    setSearchPerformed(false);
  };

  const viewMetadata = async (result) => {
    setMetadataLoading(true);
    try {
      const url = `/api/items/metadata?storageType=${result.storageType}&storageId=${encodeURIComponent(result.storageId)}&item=${encodeURIComponent(itemName)}&exact=${exactMatch ? '1' : '0'}&caseSensitive=${caseSensitive ? '1' : '0'}`;
      const response = await apiFetch(url);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch metadata');
      }

      const data = await response.json();
      setMetadataModal({
        result,
        items: Array.isArray(data) ? data : [],
        title: `${result.name} - ${result.location}`
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setMetadataLoading(false);
    }
  };

  const closeMetadataModal = () => {
    setMetadataModal(null);
  };

  const toggleIdentifierView = (key) => {
    setExpandedIdentifiers((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const copyIdentifier = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setCopiedIdentifierKey(key);
      window.setTimeout(() => {
        setCopiedIdentifierKey((current) => (current === key ? '' : current));
      }, 1400);
    } catch (err) {
      setError(err.message || 'Failed to copy value');
    }
  };

  const renderIdentifierValue = (fieldKey, value, maxLength = 28) => {
    const textValue = String(value || '');
    const isLong = textValue.length > maxLength;
    const isExpanded = Boolean(expandedIdentifiers[fieldKey]);
    const displayValue = !isLong || isExpanded ? textValue : `${textValue.slice(0, maxLength - 3)}...`;

    return (
      <div className="identifier-wrap">
        <span
          className={`info-value identifier-mono ${isLong && !isExpanded ? 'identifier-truncated' : ''}`}
          title={textValue}
        >
          {displayValue}
        </span>
        {isLong && (
          <div className="identifier-actions">
            <button type="button" className="identifier-action-btn" onClick={() => toggleIdentifierView(fieldKey)}>
              {isExpanded ? t.seeLess : t.seeMore}
            </button>
            <button type="button" className="identifier-action-btn copy" onClick={() => copyIdentifier(fieldKey, textValue)}>
              {copiedIdentifierKey === fieldKey ? t.copied : t.copy}
            </button>
          </div>
        )}
      </div>
    );
  };

  const updateAmountInput = (resultKey, value) => {
    setActionAmounts((prev) => ({ ...prev, [resultKey]: value }));
  };

  const handleItemAction = async (result, action) => {
    const resultKey = makeResultKey(result);
    const rawAmount = actionAmounts[resultKey];
    const parsedAmount = Number(rawAmount);

    if (Number.isNaN(parsedAmount) || parsedAmount < 0 || (action !== 'set' && parsedAmount <= 0)) {
      setError(t.validAmount);
      return;
    }

    try {
      setUpdatingKey(resultKey);
      setError(null);
      setSuccessMessage('');

      // For metadata searches with multiple item names, delete all matching items
      const itemsToDelete = result.metadataSearch && result.itemNames 
        ? result.itemNames 
        : [result.item || itemName];

      let data;

      // If metadata search, delete each item type found
      if (result.metadataSearch && result.itemNames) {
        for (const itemToDelete of itemsToDelete) {
          const response = await apiFetch('/api/items/update', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              storageType: result.storageType,
              storageId: result.storageId,
              item: itemToDelete,
              action,
              amount: parsedAmount
            })
          });
          
          if (!response.ok) {
            const errData = await response.json();
            throw new Error(errData.error || t.failedUpdate);
          }
        }
      } else {
        // Regular item search - use normal flow
        const response = await apiFetch('/api/items/update', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            storageType: result.storageType,
            storageId: result.storageId,
            item: result.item || itemName,
            action,
            amount: parsedAmount
          })
        });

        data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || t.failedUpdate);
        }

        setResults((prev) => prev
          .map((entry) => {
            const key = `${entry.storageType}:${entry.storageId}:${entry.item || itemName}`;
            if (key !== resultKey) {
              return entry;
            }

            return {
              ...entry,
              count: data.newCount
            };
          })
          .filter((entry) => Number(entry.count || 0) > 0)
        );
      }

      // For metadata searches, just remove the result since we deleted all items
      if (result.metadataSearch && result.itemNames) {
        setResults((prev) => prev.filter((entry) => {
          const key = `${entry.storageType}:${entry.storageId}:${entry.item || itemName}`;
          return key !== resultKey;
        }));
      }

      setActionAmounts((prev) => ({ ...prev, [resultKey]: '' }));
      
      if (result.metadataSearch && result.itemNames) {
        setSuccessMessage(`Deleted ${result.itemNames.length} item(s) from ${result.location}`);
      } else {
        setSuccessMessage(text(t.updated, {
          item: result.item || itemName,
          location: result.location,
          prev: data?.previousCount || 0,
          next: data?.newCount || 0
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingKey('');
    }
  };

  const getTotalQuantity = () => results.reduce((sum, r) => sum + parseInt(r.count || 0, 10), 0);

  const locationStats = useMemo(() => {
    const CATEGORY_META = {
      player:   { label: 'Player Inventory', icon: 'P' },
      trunk:    { label: 'Vehicle Trunk',    icon: 'T' },
      glovebox: { label: 'Vehicle Glovebox', icon: 'G' },
      stash:    { label: 'House Stash',       icon: 'S' },
    };
    const cats = {};
    results.forEach((r) => {
      const key = r.storageType || 'other';
      if (!cats[key]) {
        const meta = CATEGORY_META[key] || { label: key, icon: '-' };
        cats[key] = { label: meta.label, icon: meta.icon, hits: 0, qty: 0 };
      }
      cats[key].hits += 1;
      cats[key].qty  += parseInt(r.count || 0, 10);
    });
    return cats;
  }, [results]);

  const sortedResults = useMemo(() => {
    const sorted = [...results];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'owner':
          comparison = String(a.name || '').localeCompare(String(b.name || ''));
          break;
        case 'quantity':
          comparison = (parseInt(a.count || 0, 10) || 0) - (parseInt(b.count || 0, 10) || 0);
          break;
        case 'location':
          comparison = String(a.location || '').localeCompare(String(b.location || ''));
          break;
        case 'storageType':
          comparison = String(a.storageType || '').localeCompare(String(b.storageType || ''));
          break;
        default:
          return 0;
      }

      return sortDir === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [results, sortBy, sortDir]);

  const toggleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle direction if clicking same sort field
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // Change sort field, default to descending for quantity, ascending for others
      setSortBy(newSortBy);
      setSortDir(newSortBy === 'quantity' ? 'desc' : 'asc');
    }
  };

  return (
    <div className="item-search">
      <div className="search-hero">
        <div className="search-hero-content">
          <div className="search-icon-wrapper">
            <span className="search-icon">*</span>
          </div>
          <h1 className="search-title">{t.title}</h1>
          <p className="search-subtitle">{t.subtitle}</p>

          <form onSubmit={handleSearch} className="main-search-form">
            <div className="main-search-wrapper">
              <span className="search-icon-prefix">⌕</span>
              <input
                type="text"
                className="main-search-input"
                placeholder={t.searchPlaceholder}
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                required
              />
              {itemName && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={clearSearch}
                  title={t.clearSearch}
                >
                  x
                </button>
              )}
              <button type="submit" className="main-search-btn" disabled={loading}>
                {loading ? t.searching : t.search}
              </button>
            </div>
          </form>

          <div className="search-options">
            <label className="option-toggle">
              <input
                type="checkbox"
                checked={exactMatch}
                onChange={(e) => setExactMatch(e.target.checked)}
              />
              <span className="option-label">{t.exactMatch}</span>
            </label>
            <label className="option-toggle">
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
              />
              <span className="option-label">{t.caseSensitive}</span>
            </label>
          </div>

          {recentSearches.length > 0 && !searchPerformed && (
            <div className="recent-searches">
              <span className="recent-label">{t.recent}</span>
              {recentSearches.map((search, idx) => (
                <button
                  key={idx}
                  className="recent-chip"
                  onClick={() => handleQuickSearch(search)}
                  type="button"
                >
                  {search}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            className="advanced-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span className="toggle-icon">{showAdvanced ? '▲' : '▼'}</span>
            {t.advancedFilters}
          </button>

          {showAdvanced && (
            <div className="advanced-filters">
              <div className="filter-grid">
                <div className="filter-item">
                  <label>{t.playerIdentifier}</label>
                  <input
                    type="text"
                    placeholder={t.ownerIdentifier}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                </div>
                <div className="filter-item">
                  <label>{t.minQuantity}</label>
                  <input
                    type="number"
                    placeholder={t.minimumAmount}
                    value={filterOver}
                    onChange={(e) => setFilterOver(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="filter-item">
                  <label>{t.maxQuantity}</label>
                  <input
                    type="number"
                    placeholder={t.maximumAmount}
                    value={filterUnder}
                    onChange={(e) => setFilterUnder(e.target.value)}
                    min="0"
                  />
                </div>
                <div className="filter-item">
                  <label>
                    <input
                      type="checkbox"
                      checked={searchMetadata}
                      onChange={(e) => setSearchMetadata(e.target.checked)}
                    />
                    {t.metadataSearch}
                  </label>
                </div>
              </div>

              {searchMetadata && (
                <div className="filter-grid">
                  <div className="filter-item">
                    <label>{t.metadataField}</label>
                    <select
                      value={metadataField}
                      onChange={(e) => setMetadataField(e.target.value)}
                    >
                      <option value="serial">Serial Number</option>
                      <option value="registered">Registered To</option>
                      <option value="durability">Durability</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label>{t.metadataValue}</label>
                    <input
                      type="text"
                      placeholder="Enter value to search"
                      value={metadataValue}
                      onChange={(e) => setMetadataValue(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="search-content">
        {error && (
          <div className="alert error">
            <span className="alert-icon">!</span>
            <div>
              <strong>{t.actionFailed}</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="alert success">
            <span className="alert-icon">Done</span>
            <div>
              <strong>{t.updatedLabel}</strong>
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        {searchPerformed && !loading && !error && (
          <>
            {results.length > 0 ? (
              <>
                <div className="stats-summary">
                  <div className="stat-card">
                    <div className="stat-icon">L</div>
                    <div className="stat-content">
                      <div className="stat-value">{results.length}</div>
                      <div className="stat-label">{t.storageHits}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">C</div>
                    <div className="stat-content">
                      <div className="stat-value">{getTotalQuantity()}</div>
                      <div className="stat-label">{t.totalQuantity}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">🏷️</div>
                    <div className="stat-content">
                      <div className="stat-value">{itemName}</div>
                      <div className="stat-label">{t.item}</div>
                    </div>
                  </div>
                </div>

                <div className="location-distribution">
                  <h3>{t.distributionByLocation}</h3>
                  <div className="distribution-categories">
                    {(() => {
                      const totalQty = getTotalQuantity();
                      return Object.entries(locationStats).map(([key, cat]) => {
                        const pct = totalQty > 0 ? Math.round((cat.qty / totalQty) * 100) : 0;
                        return (
                          <div key={key} className="dist-category-row">
                            <div className="dist-category-left">
                              <span className="dist-cat-icon">{cat.icon}</span>
                              <span className="dist-cat-label">{cat.label}</span>
                            </div>
                            <div className="dist-bar-wrap">
                              <div className="dist-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="dist-category-right">
                              <span className="dist-cat-qty">{cat.qty.toLocaleString()}<span className="dist-cat-qty-label"> qty</span></span>
                              <span className="dist-cat-hits">{cat.hits} {cat.hits === 1 ? 'storage' : 'storages'}</span>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                <div className="results-header">
                  <h3>{t.liveItemControls}</h3>
                  <span className="results-count">{results.length} {results.length !== 1 ? t.results : 'result'}</span>
                </div>

                <div className="sort-controls">
                  <span className="sort-label">{t.sortBy}:</span>
                  <div className="sort-buttons">
                    <button
                      type="button"
                      className={`sort-btn ${sortBy === 'quantity' ? 'active' : ''}`}
                      onClick={() => toggleSort('quantity')}
                      title={`${t.sortQuantity} - ${sortBy === 'quantity' ? (sortDir === 'asc' ? t.ascending : t.descending) : ''}`}
                    >
                      {t.sortQuantity} {sortBy === 'quantity' && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                    <button
                      type="button"
                      className={`sort-btn ${sortBy === 'owner' ? 'active' : ''}`}
                      onClick={() => toggleSort('owner')}
                      title={`${t.sortOwner} - ${sortBy === 'owner' ? (sortDir === 'asc' ? t.ascending : t.descending) : ''}`}
                    >
                      {t.sortOwner} {sortBy === 'owner' && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                    <button
                      type="button"
                      className={`sort-btn ${sortBy === 'location' ? 'active' : ''}`}
                      onClick={() => toggleSort('location')}
                      title={`${t.sortLocation} - ${sortBy === 'location' ? (sortDir === 'asc' ? t.ascending : t.descending) : ''}`}
                    >
                      {t.sortLocation} {sortBy === 'location' && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                    <button
                      type="button"
                      className={`sort-btn ${sortBy === 'storageType' ? 'active' : ''}`}
                      onClick={() => toggleSort('storageType')}
                      title={`${t.sortStorageType} - ${sortBy === 'storageType' ? (sortDir === 'asc' ? t.ascending : t.descending) : ''}`}
                    >
                      {t.sortStorageType} {sortBy === 'storageType' && (sortDir === 'asc' ? '↑' : '↓')}
                    </button>
                  </div>
                </div>

                {!canEditItems && (
                  <div className="view-only-banner">
                    {language === 'da' ? (
                      <>Du har kun visningsadgang. Bed om <code>items.edit</code> for live-kontroller.</>
                    ) : (
                      <>You have view access only. Ask for <code>items.edit</code> to use live controls.</>
                    )}
                  </div>
                )}

                <div className="results-grid">
                  {sortedResults.map((result, index) => {
                    const resultKey = makeResultKey(result);
                    const isUpdating = updatingKey === resultKey;

                    return (
                      <div key={resultKey} className="result-card">
                        <div className="card-header">
                          <div className="card-location">
                            <span className="location-icon">
                              {result.storageType === 'player' ? '◆' : result.storageType === 'trunk' ? '◆' : result.storageType === 'stash' ? '◆' : '◆'}
                            </span>
                            <span className="location-text">{result.location}</span>
                          </div>
                          <div className="card-quantity">
                            <span className="quantity-value">{result.count}</span>
                            <span className="quantity-label">{t.qty}</span>
                          </div>
                        </div>

                        <div className="card-divider"></div>

                        <div className="card-body">
                          <div className="info-row">
                            <span className="info-icon">O</span>
                            <div className="info-content">
                              <span className="info-label">{t.owner}</span>
                              <span className="info-value">{result.name}</span>
                            </div>
                          </div>

                          <div className="info-row">
                            <span className="info-icon">ID</span>
                            <div className="info-content">
                              <span className="info-label">{t.identifier}</span>
                              {renderIdentifierValue(`${result.storageType}:${result.storageId}:identifier`, result.identifier)}
                            </div>
                          </div>

                          {(result.plate || result.storageType !== 'player') && (
                            <div className="info-row">
                              <span className="info-icon">S</span>
                              <div className="info-content">
                                <span className="info-label">{t.storageId}</span>
                                {renderIdentifierValue(`${result.storageType}:${result.storageId}:storage`, result.storageId, 22)}
                              </div>
                            </div>
                          )}

                          <div className="live-controls">
                            <h4>{t.liveActions}</h4>
                            <div className="live-controls-row">
                              <input
                                type="number"
                                min="0"
                                placeholder={t.amount}
                                value={actionAmounts[resultKey] || ''}
                                onChange={(e) => updateAmountInput(resultKey, e.target.value)}
                                disabled={!canEditItems || isUpdating}
                              />
                              <button
                                type="button"
                                className="live-btn add"
                                onClick={() => handleItemAction(result, 'add')}
                                disabled={!canEditItems || isUpdating}
                              >
                                + {t.add}
                              </button>
                              <button
                                type="button"
                                className="live-btn remove"
                                onClick={() => handleItemAction(result, 'remove')}
                                disabled={!canEditItems || isUpdating}
                              >
                                - {t.remove}
                              </button>
                              <button
                                type="button"
                                className="live-btn set"
                                onClick={() => handleItemAction(result, 'set')}
                                disabled={!canEditItems || isUpdating}
                              >
                                {t.set}
                              </button>
                            </div>
                            {isUpdating && <div className="live-updating">{t.applying}</div>}
                          </div>
                        </div>

                        <div className="card-footer">
                          <button
                            type="button"
                            className="see-more-btn"
                            onClick={() => viewMetadata(result)}
                            disabled={metadataLoading}
                            title={t.seeMore}
                          >
                            {metadataLoading ? '...' : t.seeMore}
                          </button>
                          <span className="index-badge">#{index + 1}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="no-results">
                <div className="no-results-animation">
                  <span className="no-results-icon">⌕</span>
                  <div className="no-results-ripple"></div>
                </div>
                <h2>{t.noResults}</h2>
                <p className="no-results-message">
                  {text(t.noResultsMessage, { item: itemName })}
                </p>
                <button className="new-search-btn" onClick={clearSearch}>
                  {t.newSearch}
                </button>
              </div>
            )}
          </>
        )}

        {metadataModal && (
          <div className="metadata-modal-overlay" onClick={closeMetadataModal}>
            <div className="metadata-modal" onClick={(e) => e.stopPropagation()}>
              <div className="metadata-modal-header">
                <h2>{t.metadata}</h2>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={closeMetadataModal}
                >
                  ×
                </button>
              </div>
              <div className="metadata-modal-content">
                <div className="metadata-result-info">
                  <div className="metadata-info-item">
                    <span className="metadata-label">Location:</span>
                    <span className="metadata-value">{metadataModal.result.location}</span>
                  </div>
                  <div className="metadata-info-item">
                    <span className="metadata-label">Owner:</span>
                    <span className="metadata-value">{metadataModal.result.name}</span>
                  </div>
                  <div className="metadata-info-item">
                    <span className="metadata-label">Total Items:</span>
                    <span className="metadata-value">{metadataModal.items.length}</span>
                  </div>
                </div>
                
                <div className="metadata-items-list">
                  {metadataModal.items.length > 0 ? (
                    metadataModal.items.map((item, idx) => (
                      <div key={idx} className="metadata-item-card">
                        <div className="metadata-item-header">
                          <span className="item-name">{item.name}</span>
                          <span className="item-slot">Slot {item.slot}</span>
                        </div>
                        {item.metadata ? (
                          <div className="metadata-fields">
                            {item.metadata.serial && (
                              <div className="metadata-field">
                                <span className="field-label">{t.serial}:</span>
                                <span className="field-value">{item.metadata.serial}</span>
                              </div>
                            )}
                            {item.metadata.durability !== undefined && (
                              <div className="metadata-field">
                                <span className="field-label">{t.durability}:</span>
                                <span className="field-value">{Math.round(item.metadata.durability * 10) / 10}%</span>
                              </div>
                            )}
                            {item.metadata.registered && (
                              <div className="metadata-field">
                                <span className="field-label">{t.registered}:</span>
                                <span className="field-value">{item.metadata.registered}</span>
                              </div>
                            )}
                            {item.metadata.ammo !== undefined && (
                              <div className="metadata-field">
                                <span className="field-label">{t.ammo}:</span>
                                <span className="field-value">{item.metadata.ammo}</span>
                              </div>
                            )}
                            {item.metadata.components && item.metadata.components.length > 0 && (
                              <div className="metadata-field">
                                <span className="field-label">{t.components}:</span>
                                <span className="field-value">{item.metadata.components.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="metadata-empty">{t.noMetadata}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="metadata-empty">{t.noMetadata}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ItemSearch;



